import React, {FC, useRef, useState} from 'react';
import styled from 'styled-components';

import {Trans} from '../../../../libs/shared/core/src/helpers/internationalization/react';
import {AsyncStatusesEnum} from '../../../../libs/shared/core/src/helpers/types/asyncStatuses';
import {
  buildCancelTokenSource,
  CancelError,
  CancelTokenSource,
} from '../../../../libs/shared/core/src/helpers/utilities/asyncCancelHelpers';
import {CompanyModel} from '../../../../libs/shared/core/src/models/api/company/companyModel';
import {fetchApiAsync} from '../../../../libs/shared/core/src/services/http/http';
import {
  InterfaceEventsEnum,
  trackInterfaceEvent,
} from '../../../../libs/shared/core/src/services/tracking/tracking';
import {BoxedInput} from '../../../../libs/shared/ui/src/components/boxedInputs/boxedInput';
import {
  BoxedInputElement,
  BoxedInputElementTypesEnum,
} from '../../../../libs/shared/ui/src/components/boxedInputs/boxedInputElement';
import {ChromelessButton} from '../../../../libs/shared/ui/src/components/buttons/chromelessButton';
import {Icon} from '../../../../libs/shared/ui/src/components/icon/icon';
import {ellipsis} from '../../../../libs/shared/ui/src/styles/mixins';
import {useUserTheme} from '../../../../libs/shared/ui/src/theme/userThemeProvider';
import {delayAsync} from '../../../helpers/asyncHelpers';
import {
  ApiSearchHelpCenterArticles,
  SearchHelpCenterResultModel,
} from '../../../models/api/searchHelpCenterModel';

/*
 * Props.
 */

interface HelpCenterSearchProps {
  initialQuery?: string;
  onQueryChange: (arg: string) => void;
  onResultsChange: (status: AsyncStatusesEnum, result?: SearchHelpCenterResultModel) => void;
  company: CompanyModel;
}

/*
 * Style.
 */

const StyledSearch = styled.div`
  position: relative;
  width: 100%;
`;

const IconButton = styled(ChromelessButton)`
  position: absolute;
  top: 19px;
  right: 8px;
`;

const PlaceholderDiv = styled.div`
  pointer-events: none;
  position: absolute;
  top: 19px;
  left: 8px;
  color: ${(p) => p.theme.greys.shade60};
  display: flex;
  height: 17px;
  width: 100%;
  padding-right: 20px;
`;

const PlaceholderTextDiv = styled.div`
  padding-left: 8px;
  ${ellipsis()};
`;

const StyledInput = styled(BoxedInputElement)`
  padding-right: 20px;
`;

/*
 * Component.
 */

export const HelpCenterSearch: FC<HelpCenterSearchProps> = ({
  initialQuery,
  onQueryChange,
  onResultsChange,
  company,
}) => {
  const theme = useUserTheme();
  const [query, setQuery] = useState(initialQuery || '');
  const [isFocused, setFocus] = useState(false);
  const lastCancelTokenSource = useRef<CancelTokenSource>();
  const baseSearchUrl = `${company.url}/help-center?search=`;

  const cancel = () => {
    if (lastCancelTokenSource.current) {
      lastCancelTokenSource.current.cancel();
    }
  };

  const onSearchResultsComplete = (value: string, result?: SearchHelpCenterResultModel) => {
    trackInterfaceEvent(InterfaceEventsEnum.HELP_CENTER_P4_SEARCH, {
      total_results: result ? result.count : 0,
      query: value,
    });
    onResultsChange(AsyncStatusesEnum.LOADED, result);
  };

  const fetchSearchResults = (value: string) => {
    cancel();
    onResultsChange(AsyncStatusesEnum.LOADING);

    const cancelTokenSource = buildCancelTokenSource();
    lastCancelTokenSource.current = cancelTokenSource;

    const cancelToken = cancelTokenSource.token;
    const searchTerm = encodeURIComponent(value);
    const url = `${baseSearchUrl}${searchTerm}`;
    delayAsync(500, cancelToken)
      .then(() => fetchApiAsync(ApiSearchHelpCenterArticles, url, {cancelToken}))
      .then((result) => onSearchResultsComplete(value, result))
      .catch((error) => {
        if (error instanceof CancelError) {
          return;
        }

        // Errors from forumbee are generally not retryable, design ask to just show no results.
        onResultsChange(AsyncStatusesEnum.LOADED);
      });
  };

  const onClearClick = () => {
    setQuery('');
    onQueryChange('');
  };

  const onChange = (value: string) => {
    setQuery(value);
    onQueryChange(value);
    fetchSearchResults(value);
  };

  return (
    <StyledSearch>
      <BoxedInput>
        {(childProps) => (
          <StyledInput
            {...childProps}
            type={BoxedInputElementTypesEnum.TEXT}
            value={query}
            onChange={onChange}
            onFocus={() => setFocus(true)}
            onBlur={() => setFocus(false)}
          />
        )}
      </BoxedInput>
      {!query.length && !isFocused && (
        <PlaceholderDiv>
          <Icon name="searchSmall" />
          <PlaceholderTextDiv>
            <Trans id="help-center-search-placeholder">Search Front Help Center for articles</Trans>
          </PlaceholderTextDiv>
        </PlaceholderDiv>
      )}
      {Boolean(query.length) && (
        <IconButton onClick={onClearClick}>
          <Icon name="clearSmall" color={theme.greys.shade60} hoverColor={theme.greys.shade70} />
        </IconButton>
      )}
    </StyledSearch>
  );
};
